import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  BaseSelection,
  LexicalEditor,
  RangeSelection,
} from 'lexical';

export const parseNodesFromSelection = (selection: BaseSelection | null) => {
  if (selection === null) return [];

  // If you select the very end of a node, it should not be considered part of the selection when
  // formatting the selection.
  // If the first node of the selection only has its end marker highlighted, ignore it. It does not contain any text.
  // https://github.com/facebook/lexical/blob/f989d198043980ad62fb4ca2e55166a9ac0f87f5/packages/lexical-selection/src/lexical-node.ts#L291
  const selectedNodes = selection.getNodes();
  const anchorAndFocus = selection.getStartEndPoints();
  if (anchorAndFocus === null) {
    return selectedNodes;
  }
  const firstNode = selectedNodes[0];
  if (firstNode === undefined) return selectedNodes;

  const [anchor, focus] = anchorAndFocus;
  const firstNodeTextLength = firstNode.getTextContent().length;
  const startOffset = anchor.isBefore(focus) ? anchor.offset : focus.offset;

  if ($isTextNode(firstNode) && startOffset === firstNodeTextLength) {
    return selectedNodes.slice(1);
  }
  return selectedNodes;
};

export function $checkIfFullSelection(selection: RangeSelection): boolean {
  const root = $getRoot();
  const allTextNodes = root.getAllTextNodes();
  const totalTextLength = allTextNodes.reduce((sum, node) => sum + node.getTextContent().length, 0);

  if (totalTextLength === 0) {
    return false;
  }

  const selectedTextNodes = parseNodesFromSelection(selection).filter($isTextNode);
  const anchorAndFocus = selection.getStartEndPoints();
  if (!anchorAndFocus) {
    return false;
  }

  const [anchorPoint, focusPoint] = anchorAndFocus;
  const isForward = anchorPoint.isBefore(focusPoint);
  const startPoint = isForward ? anchorPoint : focusPoint;
  const endPoint = isForward ? focusPoint : anchorPoint;
  let selectedLength = 0;

  selectedTextNodes.forEach((node) => {
    const nodeLength = node.getTextContent().length;
    const isStartNode = node.getKey() === startPoint.getNode().getKey();
    const isEndNode = node.getKey() === endPoint.getNode().getKey();

    if (isStartNode && isEndNode) {
      selectedLength += endPoint.offset - startPoint.offset;
    } else if (isStartNode) {
      selectedLength += nodeLength - startPoint.offset;
    } else if (isEndNode) {
      selectedLength += endPoint.offset;
    } else {
      selectedLength += nodeLength;
    }
  });

  return selectedLength === totalTextLength;
}

export function checkIfFullSelection(editor: LexicalEditor): boolean {
  let isFullSelection = false;

  editor.getEditorState().read(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
      return;
    }
    isFullSelection = $checkIfFullSelection(selection);
  });

  return isFullSelection;
}
